import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Check, FileText, Loader2 } from 'lucide-react';
import { Button, Modal } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { UPLOAD_ICON } from '@/shared/upload-icons';
import { BUTTON_GHOST, BUTTON_PRIMARY } from '@/shared/ui-class';
import { SharePointBrowseModal } from '@/versions/v1/chrome/chat/SharePointBrowseModal';
import {
  dialogFraming,
  dialogTitle,
  revisionCopy,
  stageLine,
  type ContributionDetail,
  type IngestJourney,
  type IngestMode,
} from './SetupScene.transform';

const STAGE_MS = 800;

/** The stepper's two controls: small neutral ghosts (comp B `.btn.gh.sm`). */
const STEP_BUTTON = cn(BUTTON_GHOST, 'h-7 rounded-lg border-[var(--hairline-strong)] px-2.5 text-xs font-semibold');

type DialogPhase = 'source' | 'processing' | 'landed';

export type IngestDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: IngestMode;
  journey: IngestJourney;
  contribution: ContributionDetail | null;
  onComplete: (fileName: string) => void;
};

export function IngestDialog({
  open,
  onOpenChange,
  mode,
  journey,
  contribution,
  onComplete,
}: IngestDialogProps) {
  const [phase, setPhase] = useState<DialogPhase>('source');
  const [fileName, setFileName] = useState<string | null>(null);
  const [stageIndex, setStageIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [sharePointOpen, setSharePointOpen] = useState(false);

  const reset = useCallback(() => {
    setPhase('source');
    setFileName(null);
    setStageIndex(0);
    setPlaying(true);
    setSharePointOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
  }, [open, journey.kind, mode, reset]);

  const acceptFile = useCallback((name: string) => {
    setFileName(name);
    setStageIndex(0);
    setPlaying(true);
    setPhase('processing');
  }, []);

  useEffect(() => {
    if (!open || phase !== 'processing' || !playing) return;
    const stages = journey.stages;
    const timer = window.setTimeout(() => {
      if (stageIndex + 1 >= stages.length) {
        setStageIndex(stages.length);
        setPhase('landed');
        return;
      }
      setStageIndex((index) => index + 1);
    }, STAGE_MS);
    return () => window.clearTimeout(timer);
  }, [open, phase, playing, stageIndex, journey.stages]);

  const stepOnce = () => {
    setPlaying(false);
    if (stageIndex + 1 >= journey.stages.length) {
      setStageIndex(journey.stages.length);
      setPhase('landed');
      return;
    }
    setStageIndex((index) => index + 1);
  };

  const finish = (name: string) => {
    onComplete(name);
  };

  const handleOpenChange = (next: boolean) => {
    if (next) {
      onOpenChange(true);
      return;
    }
    if (phase === 'landed' && fileName) {
      finish(fileName);
      return;
    }
    onOpenChange(false);
  };

  const title = phase === 'landed' ? 'What this put in the model' : dialogTitle(journey.kind, mode);
  const framing = dialogFraming(journey.kind, mode);

  return (
    <>
      <Modal
        open={open}
        onOpenChange={handleOpenChange}
        title={title}
        className="!w-[min(520px,calc(100vw-2rem))] !max-w-[520px]"
      >
        <Commentable name="IngestDialog">
          <div className="space-y-4">
            {/* The page already shows the journey (the band); the dialog carries
                only its own framing (polish.P18, comp B). */}
            <p data-ingest-framing className="-mt-2 text-xs text-muted">{framing}</p>
            {phase === 'source' ? (
              <SourceStep
                accept={journey.accept}
                onSharePoint={() => setSharePointOpen(true)}
                onFile={acceptFile}
              />
            ) : null}

            {phase === 'processing' ? (
              <ProcessingStep
                fileName={fileName}
                stages={journey.stages}
                stageIndex={stageIndex}
                playing={playing}
                onTogglePlay={() => setPlaying((value) => !value)}
                onStep={stepOnce}
              />
            ) : null}

            {phase === 'landed' ? (
              <LandedStep
                fileName={fileName}
                contribution={contribution}
                revisionNote={mode === 'update' ? revisionCopy(journey.kind) : null}
                onDone={() => finish(fileName ?? 'file')}
              />
            ) : null}
          </div>
        </Commentable>
      </Modal>
      <SharePointBrowseModal
        open={sharePointOpen}
        onOpenChange={setSharePointOpen}
        onAttach={(names) => {
          const name = names[0];
          if (name) acceptFile(name);
          setSharePointOpen(false);
        }}
      />
    </>
  );
}

function SourceStep({
  accept,
  onSharePoint,
  onFile,
}: {
  accept: string;
  onSharePoint: () => void;
  onFile: (name: string) => void;
}) {
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const onBrowse = () => fileInputRef.current?.click();

  return (
    <div className="space-y-3">
      <label
        htmlFor={inputId}
        onDragEnter={(event) => {
          event.preventDefault();
          dragDepth.current += 1;
          setDragging(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          dragDepth.current = 0;
          setDragging(false);
          const dropped = event.dataTransfer.files[0];
          if (dropped) onFile(dropped.name);
        }}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors',
          dragging
            ? 'border-zinc-400 bg-zinc-50 dark:border-zinc-500 dark:bg-zinc-900/40'
            : 'border-[var(--glass-border)] hover:border-zinc-300 dark:hover:border-zinc-600',
        )}
      >
        <UPLOAD_ICON className="h-6 w-6 text-zinc-400" />
        <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200">Drop a file here</span>
        {/* narration-ok: the dropzone's own label — the file-input idiom, not an explanation of a region */}
        <span className="text-xs text-muted">or click to browse — any file accepted</span>
      </label>
      <input
        id={inputId}
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={(event) => {
          const picked = event.target.files?.[0];
          event.target.value = '';
          if (picked) onFile(picked.name);
        }}
      />
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="ghost" onClick={onBrowse}>
          Browse
        </Button>
        <Button type="button" variant="ghost" onClick={onSharePoint}>
          From SharePoint
        </Button>
      </div>
    </div>
  );
}

function ProcessingStep({
  fileName,
  stages,
  stageIndex,
  playing,
  onTogglePlay,
  onStep,
}: {
  fileName: string | null;
  stages: IngestJourney['stages'];
  stageIndex: number;
  playing: boolean;
  onTogglePlay: () => void;
  onStep: () => void;
}) {
  return (
    <div className="space-y-4">
      {fileName ? (
        <FileEcho name={fileName} />
      ) : null}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={STEP_BUTTON}
          onClick={onTogglePlay}
          aria-label={playing ? 'Pause reconstruction' : 'Resume reconstruction'}
        >
          {playing ? 'Pause' : 'Resume'}
        </button>
        <button type="button" className={STEP_BUTTON} onClick={onStep} aria-label="Step to next stage">
          Step
        </button>
      </div>
      <ol className="space-y-2" aria-live="polite">
        {stages.map((stage, index) => {
          const done = index < stageIndex;
          const current = index === stageIndex && stageIndex < stages.length;
          return (
            <li
              key={stage.id}
              className={cn(
                'flex items-start gap-3 rounded-lg px-2 py-1.5 text-sm',
                current ? 'bg-zinc-50 dark:bg-zinc-900/40' : '',
              )}
            >
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-zinc-400">
                {done ? (
                  <Check className="h-3.5 w-3.5 text-zinc-900 dark:text-zinc-100" aria-hidden />
                ) : current ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-zinc-300 dark:bg-zinc-600" />
                )}
              </span>
              <span
                className={cn(
                  current
                    ? 'font-[550] text-zinc-900 dark:text-zinc-100'
                    : done
                      ? 'text-zinc-600 dark:text-zinc-400'
                      : 'text-muted',
                )}
              >
                {stageLine(stage, done || current)}
                {stage.detail ? (
                  <span className="mt-0.5 block text-xs text-muted">{stage.detail}</span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function LandedStep({
  fileName,
  contribution,
  revisionNote,
  onDone,
}: {
  fileName: string | null;
  contribution: ContributionDetail | null;
  revisionNote: string | null;
  onDone: () => void;
}) {
  return (
    <div className="space-y-4">
      {fileName ? <FileEcho name={fileName} /> : null}
      {contribution?.lines.length ? (
        <ul className="space-y-1.5 text-sm text-muted">
          {contribution.lines.map((line) => (
            <li key={line.label}>
              {line.label}:{' '}
              <span className="tabular text-zinc-800 dark:text-zinc-200">{line.value}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {contribution?.note ? <p className="text-xs text-muted">{contribution.note}</p> : null}
      {revisionNote ? <p className="text-xs text-muted">{revisionNote}</p> : null}
      <div className="flex justify-end">
        <button type="button" className={BUTTON_PRIMARY} onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}

function FileEcho({ name }: { name: string }) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-[var(--glass-border)] px-2 py-2.5 text-sm">
      <FileText className="h-4 w-4 flex-shrink-0 text-zinc-400" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-zinc-900 dark:text-zinc-100">{name}</div>
        <div className="text-xs text-muted">Staged for reconstruction</div>
      </div>
    </div>
  );
}
